import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { FiSearch, FiEdit2, FiTrash2 } from "react-icons/fi";
import { IoChevronDown } from "react-icons/io5";
import { ModuleLayout } from "../../../layouts";
import { ContentCard, Button, Pagination } from "../../../components";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import {
  StatCard,
  StatusBadge,
  ModuleBadge,
  SectionHeader,
  DataTable,
  ActionIconButton,
  ConfirmModal,
} from "../components";
import { getPermissionsApi, deletePermissionApi, getModulesApi } from "../../../DAL";

const PermissionsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const TABLE_COLS = [t('permissions.colPermission'), t('permissions.colCode'), t('permissions.colModule'), t('common.status'), t('common.lastActive'), t('common.actions')];
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Data ────────────────────────────────────────────────────────────────────
  const { data: permissionsData, isLoading } = useQuery({
    queryKey: [QUERY_CONSTANTS.PERMISSIONS, page],
    queryFn: () => getPermissionsApi(page),
  });
  const permissions = permissionsData?.data ?? [];
  const pagination = permissionsData?.pagination ?? {};

  const { data: modulesData } = useQuery({
    queryKey: [QUERY_CONSTANTS.MODULES],
    queryFn: getModulesApi,
  });
  const moduleNames = (modulesData?.data?.modules ?? []).map((m) => m.name);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const totalActive = permissions.filter((p) => p.is_active).length;
  const uniqueModules = new Set(permissions.map((p) => p.module)).size;
  const stats = [
    { value: pagination.total ?? permissions.length, label: t('permissions.statTotal') },
    { value: totalActive, label: t('common.active') },
    { value: uniqueModules, label: t('permissions.statModules') },
  ];

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filtered = permissions.filter((p) => {
    const matchSearch =
      !search ||
      p.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.name?.toLowerCase().includes(search.toLowerCase());
    const matchModule = !moduleFilter || p.module === moduleFilter;
    return matchSearch && matchModule;
  });

  // ── Delete ───────────────────────────────────────────────────────────────────
  const { mutate: deletePermission, isPending: isDeleting } = useMutation({
    mutationFn: () => deletePermissionApi(deleteTarget.id),
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: [QUERY_CONSTANTS.PERMISSIONS] });
      setDeleteTarget(null);
    },
  });

  return (
    <ModuleLayout
      title={t('permissions.title')}
      onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_DASHBOARD)}
    >
      <p className="text-sm text-gray-500 mb-4">{t('permissions.breadcrumb')}</p>

      <ContentCard>
        <SectionHeader
          title={t('permissions.sectionTitle')}
          subtitle={t('permissions.sectionSubtitle')}
          action={
            <Button onClick={() => navigate(ROUTES_CONSTANTS.CREATE_PERMISSION)}>
              {t('permissions.btnAdd')}
            </Button>
          }
        />

        {/* Stats */}
        <div className="flex gap-4 mb-6">
          {stats.map((s) => (
            <StatCard key={s.label} value={s.value} label={s.label} />
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('common.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full border border-[#E3E3E3] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary-500"
            />
          </div>
          <div className="relative">
            <select
              value={moduleFilter}
              onChange={(e) => { setModuleFilter(e.target.value); setPage(1); }}
              className="appearance-none border border-[#E3E3E3] rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none cursor-pointer"
            >
              <option value="">{t('permissions.allModules')}</option>
              {moduleNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <IoChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
          </div>
        </div>

        <DataTable columns={TABLE_COLS} isLoading={isLoading}>
          {filtered.map((p) => (
            <tr key={p.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-black">{p.display_name}</p>
                {p.description && (
                  <p className="text-xs text-gray-400">{p.description}</p>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">{p.name}</td>
              <td className="px-4 py-3">
                <ModuleBadge label={p.module} />
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={p.is_active ? "Active" : "Inactive"} />
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {p.updated_at
                  ? new Date(p.updated_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <ActionIconButton
                    icon={FiEdit2}
                    onClick={() =>
                      navigate(ROUTES_CONSTANTS.UPDATE_PERMISSION, {
                        state: { id: p.id },
                      })
                    }
                  />
                  <ActionIconButton
                    icon={FiTrash2}
                    onClick={() => setDeleteTarget(p)}
                    danger
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>

        <Pagination
          currentPage={pagination.current_page ?? page}
          lastPage={pagination.last_page}
          onPageChange={setPage}
        />
      </ContentCard>

      {deleteTarget && (
        <ConfirmModal
          title={t('permissions.deleteTitle')}
          confirmLabel={t('common.delete')}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={deletePermission}
          loading={isDeleting}
        >
          <p>
            {t('permissions.deleteConfirmPre')}{" "}
            <span className="text-primary-500 font-medium">
              [{deleteTarget.display_name}]
            </span>
            ? {t('common.undone')}
          </p>
        </ConfirmModal>
      )}
    </ModuleLayout>
  );
};

export default PermissionsList;
